<script setup lang="ts">
import { adjustForCustomers } from '@revolugo/common/cancellation-policies'
import { dayjs } from '@revolugo/common/utils'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'

import { RBadge } from '#atoms'
import { CancellationPoliciesStatus } from '#types'

import type { CancellationPoliciesTimelineProps } from './RCancellationPoliciesTimeline.types.ts'
import type { CancellationPolicy } from '@revolugo/graphql/private'

const { locale, t } = useI18n()

const props = defineProps<CancellationPoliciesTimelineProps>()

const adjustedCancellationPolicies = computed(() =>
  adjustForCustomers(props.cancellationPolicies),
)

function isCurrentDateBetween(dateFrom: string, dateTo: string): boolean {
  return dayjs().isBetween(dateFrom, dateTo, undefined, '[)')
}

function getBadgePosition(dateFrom: string, dateTo: string): string {
  const totalDays = dayjs(dateTo).diff(dateFrom, 'day')
  const daysFromstart = dayjs().diff(dateFrom, 'day')
  return `${(daysFromstart / totalDays) * 100}%`
}

const cancellationPoliciesStepsProps: {
  [key: string]: {
    line: string
    chip: string
    label: string
    border: string
  }
} = {
  [CancellationPoliciesStatus.Free]: {
    border: 'border-green-700',
    chip: 'bg-green-50 text-green-700',
    label: 'FREE',
    line: 'bg-green-600',
  },
  [CancellationPoliciesStatus.NoRefund]: {
    border: 'border-red-700',
    chip: 'bg-red-100 text-red-700',
    label: 'NO_REFUND',
    line: 'bg-red-600',
  },
  [CancellationPoliciesStatus.Partial]: {
    border: 'border-orange-600',
    chip: 'bg-orange-50 text-orange-700',
    label: 'PARTIAL',
    line: 'bg-orange-500',
  },
}

function getCancellationPoliciesLabels(penaltyPercentage: number): string {
  switch (penaltyPercentage) {
    case 0:
      return cancellationPoliciesStepsProps[CancellationPoliciesStatus.Free]
        .label
    case 100:
      return cancellationPoliciesStepsProps[CancellationPoliciesStatus.NoRefund]
        .label
    default:
      return cancellationPoliciesStepsProps[CancellationPoliciesStatus.Partial]
        .label
  }
}

function getCancellationPoliciesStepsProps({
  dateFrom,
  dateTo,
  penaltyPercentage,
}: CancellationPolicy): {
  line: string
  chip: string
  border: string
} {
  if (isCurrentDateBetween(dateFrom, dateTo)) {
    switch (penaltyPercentage) {
      case 0:
        return cancellationPoliciesStepsProps[CancellationPoliciesStatus.Free]
      case 100:
        return cancellationPoliciesStepsProps[
          CancellationPoliciesStatus.NoRefund
        ]
      default:
        return cancellationPoliciesStepsProps[
          CancellationPoliciesStatus.Partial
        ]
    }
  } else {
    return {
      border: 'border-neutral-100',
      chip: '',
      line: 'bg-white',
    }
  }
}
</script>
<template>
  <div
    id="timeline"
    class="flex items-stretch w-full"
    data-testid="r-cancellation-policies-timeline"
    v-bind="$attrs"
  >
    <div
      class="step w-full flex flex-col content-end items-stretch align-middle"
      data-testid="r-cancellation-policies-timeline-step"
      v-for="(cancellationPolicy, index) in adjustedCancellationPolicies"
      :key="index"
    >
      <p
        v-if="cancellationPolicies.length > 1"
        class="-translate-x-1/2 text-center text-sm mb-1 font-medium"
        :class="{ 'text-transparent': index === 0 }"
      >
        {{
          dayjs(cancellationPolicy.dateFrom).locale(locale).format('MMM D H:mm')
        }}
      </p>
      <div
        class="flex flex-1 flex-wrap content-start items-start justify-center"
      >
        <div
          :data-testid="`r-cancellation-policies-timeline-item-${index}`"
          class="step-line border-r-solid border-r-2 w-full h-3 mb-2 relative border"
          :class="{
            'rounded-l-full': index === 0,
            'rounded-r-full': index === cancellationPolicies.length - 1,
            [getCancellationPoliciesStepsProps(cancellationPolicy).line]: true,
            [getCancellationPoliciesStepsProps(cancellationPolicy).border]:
              true,
          }"
        >
          <RBadge
            v-if="
              isCurrentDateBetween(
                cancellationPolicy.dateFrom,
                cancellationPolicy.dateTo,
              )
            "
            :data-testid="`r-cancellation-policies-timeline-item-badge-${index}`"
            v-tooltip.top="t('today')"
            class="border-solid border-2 border-neutral-500 rounded-full absolute -translate-y-1/4 z-10 -ml-2 bg-white"
            :style="{
              left: getBadgePosition(
                cancellationPolicy.dateFrom,
                cancellationPolicy.dateTo,
              ),
            }"
            size="small"
          />
        </div>
        <span
          :data-testid="`r-cancellation-policies-timeline-item-chip-${index}`"
          :class="getCancellationPoliciesStepsProps(cancellationPolicy).chip"
          class="rounded px-1 py-0.5 text-sm text-center"
        >
          {{
            t(
              getCancellationPoliciesLabels(
                cancellationPolicy.penaltyPercentage,
              ),
            )
          }}
        </span>
      </div>
    </div>
  </div>
</template>

<i18n src="./RCancellationPoliciesTimeline.i18n.json"></i18n>
